import { modals } from '@mantine/modals'
import { Button, type ButtonProps } from '@ruguoapp/jike-fe-components'
import clsx from 'clsx'
import { merge } from 'lodash-es'
import type { MouseEvent } from 'react'
import ModalContent from './ModalContent'
import type { ModalProps } from './types'

export function showModal(
	title: ModalProps['title'],
	content?: ModalProps['children'],
	options?: Omit<ModalProps, 'isShow' | 'title' | 'children'>
) {
	const { footer, header, className, ...restOptions } = options ?? {}
	return modals.open({
		children: (
			<ModalContent header={header} footer={footer} title={title} isShow>
				{content}
			</ModalContent>
		),
		overlayProps: {
			className: 'jk-modal-backdrop',
			transitionProps: {
				transition: 'fade',
			},
		},
		centered: true,
		withCloseButton: false,
		fullScreen: true,
		trapFocus: false,
		className: clsx('jk-modal', className),
		...restOptions,
	})
}

export function hideModal() {
	modals.closeAll()
}

export interface ModalConfirmOptions
	extends Omit<ModalProps, 'title' | 'children' | 'footer' | 'isShow' | 'onClose'> {
	cancelButtonProps?: ButtonProps
	confirmButtonProps?: ButtonProps
	onClose?: () => void
}

export interface ModalAlertOptions
	extends Omit<ModalProps, 'title' | 'children' | 'footer' | 'isShow' | 'onClose'> {
	buttonProps?: ButtonProps
	onClose?: () => void
}

export async function showConfirm(
	title: ModalProps['title'],
	content?: ModalProps['children'],
	options?: ModalConfirmOptions
) {
	return new Promise<void>((resolve, reject) => {
		const { cancelButtonProps, confirmButtonProps, onClose, ...rest } = merge(
			{},
			{
				cancelButtonProps: {
					children: '再想想',
					appearance: 'ghost',
				} as ButtonProps,
				confirmButtonProps: {
					children: '确定',
					appearance: 'primary',
				} as ButtonProps,
			},
			options
		)

		showModal(title, content, {
			...rest,
			onClose: () => {
				onClose?.()
				reject()
				hideModal()
			},
			footer: (
				<>
					<Button
						{...cancelButtonProps}
						key="jk-modal-cancel-btn"
						onClick={(e: MouseEvent<HTMLButtonElement>) => {
							cancelButtonProps?.onClick?.(e)
							reject()
							hideModal()
						}}
					/>
					<Button
						{...confirmButtonProps}
						key="jk-modal-confirm-btn"
						onClick={(e: MouseEvent<HTMLButtonElement>) => {
							confirmButtonProps?.onClick?.(e)
							resolve()
							hideModal()
						}}
					/>
				</>
			),
		})
	})
}

export async function showAlert(
	title: ModalProps['title'],
	content?: ModalProps['children'],
	options?: ModalAlertOptions
) {
	return new Promise<void>((resolve) => {
		const { buttonProps, onClose, ...rest } = merge(
			{},
			{
				buttonProps: {
					children: '好的',
					appearance: 'primary',
				} as ButtonProps,
			},
			options
		)

		showModal(title, content, {
			...rest,
			onClose: () => {
				onClose?.()
				resolve()
				hideModal()
			},
			footer: (
				<Button
					{...buttonProps}
					key="jk-modal-alert-btn"
					onClick={(e: MouseEvent<HTMLButtonElement>) => {
						buttonProps?.onClick?.(e)
						resolve()
						hideModal()
					}}
				/>
			),
		})
	})
}
